import React, { useState } from "react";
import { MailCheck, Clock, LogOut, RefreshCw } from "lucide-react";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "../firebase/config";
import { useAuth } from "../hooks/useAuth";

/**
 * Schermata per chi è autenticato ma non può ancora entrare.
 *
 * Distingue i due motivi, perché richiedono azioni diverse:
 *  - email non verificata → l'utente può agire subito (reinvia, poi conferma)
 *  - in attesa di approvazione → non c'è niente da fare se non aspettare
 */
export default function PendingApprovalPage({ emailVerified }) {
  const { user, signOut } = useAuth();
  const [inviata, setInviata] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [errore, setErrore] = useState("");

  const reinvia = async () => {
    setErrore("");
    try {
      await sendEmailVerification(auth.currentUser);
      setInviata(true);
    } catch (err) {
      setErrore(
        err?.code === "auth/too-many-requests"
          ? "Troppi invii ravvicinati. Riprova fra qualche minuto."
          : "Non è stato possibile inviare l'email. Riprova."
      );
    }
  };

  // Dopo il clic sul link, il token in memoria dice ancora "non verificato":
  // senza questo pulsante l'utente resterebbe bloccato pur avendo verificato.
  const hoVerificato = async () => {
    setVerificando(true);
    setErrore("");
    try {
      await auth.currentUser.reload();
      await auth.currentUser.getIdToken(true);
      window.location.reload();
    } catch (err) {
      setErrore("Non è stato possibile aggiornare lo stato. Riprova.");
      setVerificando(false);
    }
  };

  const Icona = emailVerified ? Clock : MailCheck;

  return (
    <div className="min-h-screen bg-neutral-950 px-4 py-10 text-neutral-100">
      <div className="mx-auto w-full max-w-md">
        <div className="rounded-2xl border border-neutral-800 bg-neutral-900 p-6">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-forfy-500/10 text-forfy-500">
            <Icona className="h-6 w-6" />
          </div>

          {!emailVerified ? (
            <>
              <h1 className="text-lg font-semibold">Verifica la tua email</h1>
              <p className="mt-2 text-sm text-neutral-400">
                Ti abbiamo inviato un link a <strong className="text-neutral-200">{user?.email}</strong>.
                Aprilo per confermare che l'indirizzo è tuo, poi torna qui.
              </p>

              <div className="mt-5 flex flex-col gap-2">
                <button
                  onClick={hoVerificato}
                  disabled={verificando}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-forfy-500 px-4 text-sm font-medium text-white transition hover:bg-forfy-600 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${verificando ? "animate-spin" : ""}`} />
                  {verificando ? "Controllo…" : "Ho verificato l'email"}
                </button>
                <button
                  onClick={reinvia}
                  disabled={inviata}
                  className="min-h-11 rounded-lg border border-neutral-700 px-4 text-sm text-neutral-300 transition hover:bg-neutral-800 disabled:opacity-50"
                >
                  {inviata ? "Email inviata" : "Reinvia l'email di verifica"}
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold">Richiesta in attesa di approvazione</h1>
              <p className="mt-2 text-sm text-neutral-400">
                Forfy è in beta privata e ogni accesso viene approvato a mano. La tua
                richiesta per <strong className="text-neutral-200">{user?.email}</strong> è
                registrata: riceverai un'email appena sarà attivata.
              </p>
              <p className="mt-3 text-sm text-neutral-500">
                Se pensi che sia un errore, scrivi a{" "}
                <a href="mailto:info@forfy.it" className="text-forfy-500 hover:underline">
                  info@forfy.it
                </a>
                .
              </p>
            </>
          )}

          {errore && (
            <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
              {errore}
            </div>
          )}

          <button
            onClick={signOut}
            className="mt-6 flex min-h-11 w-full items-center justify-center gap-2 text-sm text-neutral-500 transition hover:text-neutral-300"
          >
            <LogOut className="h-4 w-4" />
            Esci
          </button>
        </div>
      </div>
    </div>
  );
}
